import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { InsiVerificationBatchRequestDTO } from '@services/InsiService'
import {
  createInsiBatchCheckRequest,
  deleteInsiBatchCheckRequest,
  findPendingInsiBatchRequests,
  updateInsiBatchCheckRequestResult,
  getInsiBatchResult,
} from '@state/thunks/insiThunk'

export interface InsiState {
  pendingBatchRequests: InsiVerificationBatchRequestDTO[]
}

const initialState: InsiState = {
  pendingBatchRequests: [],
}

const insiSlice = createSlice({
  name: 'insi',
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(
        findPendingInsiBatchRequests.fulfilled,
        (
          state,
          { payload }: PayloadAction<InsiVerificationBatchRequestDTO[]>,
        ) => {
          state.pendingBatchRequests = payload
        },
      )
      .addCase(
        createInsiBatchCheckRequest.fulfilled,
        (
          state,
          { payload }: PayloadAction<InsiVerificationBatchRequestDTO>,
        ) => {
          const index = state.pendingBatchRequests.findIndex(
            (request) => request.batchId === payload.batchId,
          )

          const nextEntry: InsiVerificationBatchRequestDTO = {
            ...payload,
            result: payload.result ?? undefined,
          }

          if (index >= 0) {
            state.pendingBatchRequests[index] = {
              ...nextEntry,
              ...state.pendingBatchRequests[index],
            }
            return
          }

          state.pendingBatchRequests = [
            nextEntry,
            ...state.pendingBatchRequests,
          ]
        },
      )
      .addCase(getInsiBatchResult.fulfilled, (state, { payload }) => {
        const { batchId, result } = payload
        const index = state.pendingBatchRequests.findIndex(
          (request) => request.batchId === batchId,
        )

        if (index === -1) {
          state.pendingBatchRequests.push({
            batchId,
            delay: 0,
            dateToStartResultCheck: '',
            codeReturned: '',
            submittedList: [],
            createdAt: new Date().toISOString(),
            result,
          })
          return
        }

        state.pendingBatchRequests[index] = {
          ...state.pendingBatchRequests[index],
          result,
        }
      })
      .addCase(updateInsiBatchCheckRequestResult.fulfilled, (state, action) => {
        const { payload } = action
        const batchId = payload?.batchId ?? action.meta?.arg?.batchId ?? ''

        const index = state.pendingBatchRequests.findIndex(
          (request) => request.batchId === batchId,
        )
        if (index === -1) {
          state.pendingBatchRequests.push({
            ...payload,
            batchId,
            result:
              payload?.result ?? action.meta?.arg?.payload?.result ?? undefined,
          })
          return
        }

        const existing = state.pendingBatchRequests[index]
        state.pendingBatchRequests[index] = {
          ...existing,
          ...payload,
          batchId,
          result:
            payload?.result ??
            action.meta?.arg?.payload?.result ??
            existing.result ??
            undefined,
        }
      })
      .addCase(
        deleteInsiBatchCheckRequest.fulfilled,
        (state, { payload }: PayloadAction<string>) => {
          state.pendingBatchRequests = state.pendingBatchRequests.filter(
            (request) => request.batchId !== payload,
          )
        },
      ),
})

export default insiSlice.reducer
